//@ts-nocheck
import { parseISO, format, add, isSameMonth, isSameYear } from 'date-fns';

/**
 * Returns a formatted string representing the start and end dates of a trip.
 * If the start and end dates are in the same month and year, the format is "MMMM d-d, yyyy".
 * If the start and end dates are in the same year but different months, the format is "MMM d - MMM d, yyyy".
 * If the start and end dates are in different years, the format is "MMM d, yyyy - MMM d, yyyy".
 * @param {string} startdate - The start date of the trip in ISO format (YYYY-MM-DDTHH:MM:SSZ)
 * @param {number} duration - The duration of the trip in days (default is 1)
 * @returns {string} - The formatted string representing the start and end dates of the trip
 */
export const getFormattedTripDates = (startdate, duration = 1) => {
  if (!startdate) return '';

  // Extracts only the date (YYYY-MM-DD)
  const startDate = parseISO(startdate.split('T')[0]);
  const endDate = add(startDate, { days: duration - 1 });

  // Check if dates are in the same month and year
  if (isSameMonth(startDate, endDate) && isSameYear(startDate, endDate)) {
    // Same month format: "July 22-24, 2024"
    const startDay = format(startDate, 'd');
    const endDay = format(endDate, 'd');

    return `${format(startDate, 'MMMM')} ${startDay}-${endDay}, ${format(startDate, 'yyyy')}`;
  }

  // Check if dates are in the same year but different months
  if (isSameYear(startDate, endDate)) {
    // Different months, same year: "Jan 5 - Feb 15, 2026"
    const startMonthDay = format(startDate, 'MMM d');
    const endMonthDay = format(endDate, 'MMM d');
    const year = format(startDate, 'yyyy');

    return `${startMonthDay} - ${endMonthDay}, ${year}`;
  }

  // Different years: "Dec 28, 2025 - Jan 5, 2026"
  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
};

/**
 * Determines if a trip start date has passed (trip has started)
 * Trip is considered started at 12:01 AM on the departure date in user's local timezone
 *
 * @param {string} tripStartDate - ISO date string (e.g., "2026-01-05T00:00:00.000Z")
 * @returns {boolean} - true if trip has started, false if trip hasn't started yet
 */
export const hasTripStarted = (tripStartDate) => {
  try {
    if (!tripStartDate) {
      return true; // If no date provided, assume trip has started (hide countdown)
    }

    // Parse the trip start date
    const tripDate = parseISO(tripStartDate?.split('T')?.[0]);

    // Check if date is valid
    if (isNaN(tripDate.getTime())) {
      console.warn('Invalid trip start date provided:', tripStartDate);
      return true; // Hide countdown for invalid dates
    }

    // Set trip start time to 12:01 AM on the departure date in user's local timezone
    const tripStartTime = new Date(
      tripDate.getFullYear(),
      tripDate.getMonth(),
      tripDate.getDate(),
      0, // 12 AM (midnight)
      1, // 1 minute past midnight
      0, // 0 seconds
      0 // 0 milliseconds
    );

    // Get current time in user's local timezone
    const now = new Date();

    // Return true if current time is past the trip start time
    return now.getTime() >= tripStartTime.getTime();
  } catch (error) {
    console.error('Error checking if trip has started:', error);
    return true; // Hide countdown on error
  }
};

/**
 * Gets the exact trip start time (12:01 AM on departure date)
 * Useful for debugging or additional date calculations
 *
 * @param {string} tripStartDate - ISO date string
 * @returns {Date|null} - Date object representing trip start time, or null if invalid
 */
export const getTripStartTime = (tripStartDate) => {
  try {
    if (!tripStartDate) return null;

    const tripDate = parseISO(tripStartDate?.split('T')?.[0]);

    if (isNaN(tripDate.getTime())) {
      return null;
    }

    return new Date(
      tripDate.getFullYear(),
      tripDate.getMonth(),
      tripDate.getDate(),
      0, // 12 AM (midnight)
      1, // 1 minute past midnight
      0, // 0 seconds
      0 // 0 milliseconds
    );
  } catch (error) {
    console.error('Error getting trip start time:', error);
    return null;
  }
};

/**
 * Calculates time remaining until trip starts
 * Returns null if trip has already started
 *
 * @param {string} tripStartDate - ISO date string
 * @returns {Object|null} - Object with days, hours, minutes, seconds or null if trip started
 */
export const getTimeUntilTrip = (tripStartDate) => {
  try {
    if (hasTripStarted(tripStartDate)) {
      return null;
    }

    const tripStartTime = getTripStartTime(tripStartDate);
    if (!tripStartTime) return null;

    const now = new Date();
    const difference = tripStartTime.getTime() - now.getTime();

    if (difference <= 0) {
      return null;
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  } catch (error) {
    console.error('Error calculating time until trip:', error);
    return null;
  }
};
